if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.lastAnnouncedSection = null;
        this.lastRenderedUrl = null;
        this.announceToken = 0;
        this.statusMessageTimeout = null;
        this.resultPress = null;
        this.resultRetainTimeout = null;
        this.focusOutTimeout = null;
        this.pendingTab = false;
        this.keepResultsOpen = false;
        this.focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

        this.onWindowResultPointerUp = this.onWindowResultPointerUp.bind(this);
        this.onWindowResultPointerCancel = this.onWindowResultPointerCancel.bind(this);
        this.onWindowPageShow = this.onWindowPageShow.bind(this);
        this.onPageTabKeydown = this.onPageTabKeydown.bind(this);

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', this.onInput.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
        this.input.addEventListener('keydown', this.onInputKeydown.bind(this));
        this.addEventListener('focusin', this.onFocusIn.bind(this));
        this.addEventListener('focusout', this.onFocusOut.bind(this));
        this.addEventListener('keydown', this.onTabKeydown.bind(this));
        this.addEventListener('keydown', this.onResultsKeydown.bind(this));
        this.addEventListener('pointerdown', this.onResultPointerDown.bind(this));

        this.updateResetButtonState();
        this.syncResultsState();
        this.syncOverlayState(false);
      }

      connectedCallback() {
        if (!this.pageshowBound) {
          this.pageshowBound = true;
          window.addEventListener('pageshow', this.onWindowPageShow);
        }

        if (this.isCollectionSearch() && !this.pageTabBound) {
          this.pageTabBound = true;
          document.addEventListener('keydown', this.onPageTabKeydown, true);
        }
      }

      disconnectedCallback() {
        if (this.pageshowBound) {
          window.removeEventListener('pageshow', this.onWindowPageShow);
          this.pageshowBound = false;
        }

        if (this.pageTabBound) {
          document.removeEventListener('keydown', this.onPageTabKeydown, true);
          this.pageTabBound = false;
        }

        this.clearResultPress();
        this.pendingTab = false;
        this.keepResultsOpen = false;
        this.holdStatusAnnouncement();
        if (this.focusOutTimeout) {
          clearTimeout(this.focusOutTimeout);
          this.focusOutTimeout = null;
        }
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      get recommendationContainer() {
        return this.querySelector('.search__recommendation');
      }

      get resultsContainer() {
        return document.getElementById('PredictiveSearchResults-' + theme.utils.sectionId(this));
      }

      get sectionStatus() {
        return this.querySelector('[data-predictive-search-status], [data-predictive-search-section-status]');
      }

      getFocusableItems(container) {
        if (!container) return [];

        return [...container.querySelectorAll(this.focusableSelector)].filter((item) => {
          if (item.closest('[hidden]')) return false;
          if (item.closest('[inert]')) return false;
          if (item.getAttribute('aria-hidden') === 'true') return false;
          if (item.tabIndex === -1) return false;

          return true;
        });
      }

      getResultLinkCount() {
        if (!this.resultsContainer) return 0;

        return [...this.resultsContainer.querySelectorAll('[data-predictive-search-section]')].reduce(
          (count, section) => count + this.getFocusableItems(section).length,
          0
        );
      }

      get suggestionItems() {
        const suggestionsContainer = this.resultsContainer?.querySelector('[data-predictive-search-section="suggestions"]');
        return this.getFocusableItems(suggestionsContainer);
      }

      get resultItems() {
        return this.getFocusableItems(this.resultsContainer);
      }

      isCollectionSearch() {
        return Boolean(this.closest('.collection'));
      }

      open() {
        if (this.isCollectionSearch()) {
          document.body.classList.add('predictive-search-open');
          this.syncOverlayState(true);
        }
      }

      close() {
        this.setBusyState(false);
        this.setResultsState(false);
        this.resetSectionAnnouncement();

        if (this.isCollectionSearch()) {
          document.body.classList.remove('predictive-search-open');
          this.syncOverlayState(false);
        }
      }

      get overlay() {
        return this.querySelector('predictive-search-overlay');
      }

      syncOverlayState(isOpen) {
        const overlay = this.overlay;
        if (!overlay) return;

        overlay.toggleAttribute('inert', !isOpen);
        overlay.toggleAttribute('hidden', !isOpen);
      }

      updateExpandedState() {
        this.input.setAttribute('aria-expanded', this.hasAttribute('results') ? 'true' : 'false');
      }

      syncResultsState() {
        this.updateBoxFocusability();
        this.updateExpandedState();
        this.updateSectionFirstLinkDescriptions();
      }

      setResultsState(isVisible) {
        this.toggleAttribute('results', isVisible);
        this.syncResultsState();

        if (isVisible) {
          this.announceResultsCount();
        }
      }

      setBusyState(isBusy) {
        this.toggleAttribute('loading', isBusy);
        this.resultsContainer?.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      }

      updateResetButtonState() {
        const hasQuery = this.getQuery().length > 0;

        this.resetButton.disabled = !hasQuery;
        this.resetButton.tabIndex = hasQuery ? 0 : -1;
      }

      updateBoxFocusability() {
        const showResults = this.hasAttribute('results');
        const showRecommendations = !showResults && (!this.isCollectionSearch() || this.contains(document.activeElement));

        if (this.recommendationContainer) {
          this.recommendationContainer.toggleAttribute('inert', !showRecommendations);
        }

        if (this.resultsContainer) {
          this.resultsContainer.toggleAttribute('inert', !showResults);
        }
      }

      focusResultItem(index) {
        this.moveFocusInside(this.resultItems[index]);
      }

      getResultItemIndex(target) {
        return this.resultItems.findIndex((item) => item === target || item.contains(target));
      }

      moveFocusInside(element) {
        if (!element) return;

        this.keepResultsOpen = true;
        this.pendingTab = false;
        this.open();
        element.focus();
        this.keepResultsOpen = false;
      }

      isInsideClosedDetailsContent(element) {
        let node = element.parentElement;

        while (node && node.nodeType === Node.ELEMENT_NODE && node !== document.documentElement) {
          if (node.localName === 'details' && !node.open) {
            const summary = node.querySelector(':scope > summary');
            if (element !== summary && !summary?.contains(element)) {
              return true;
            }
          }

          node = node.parentElement;
        }

        return false;
      }

      isPageFocusable(element) {
        if (!element || element.disabled) return false;
        if (element.closest('[hidden]')) return false;
        if (element.closest('[inert]')) return false;
        if (element.getAttribute('aria-hidden') === 'true') return false;
        if (element.tabIndex < 0) return false;
        if (this.isInsideClosedDetailsContent(element)) return false;

        let node = element;
        while (node && node.nodeType === Node.ELEMENT_NODE && node !== document.documentElement) {
          const style = window.getComputedStyle(node);
          if (style.display === 'none' || style.visibility === 'hidden') return false;
          node = node.parentElement;
        }

        return true;
      }

      getPageFocusables() {
        const selector =
          'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
        const following = [];
        const stickyFollowing = [];

        for (const element of document.querySelectorAll(selector)) {
          if (this.contains(element)) continue;
          if ((this.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING) === 0) continue;
          if (!this.isPageFocusable(element)) continue;

          if (element.closest('facet-sticky')) {
            stickyFollowing.push(element);
          } else {
            following.push(element);
          }
        }

        // Sticky "Show filters" is in the DOM before Sort and the grid but is
        // visually a bottom control. Prefer the next in-flow control so Tab
        // does not jump over Sort, the product grid, or footer links.
        return following.concat(stickyFollowing);
      }

      getNextPageFocusable() {
        return this.getPageFocusables()[0] || null;
      }

      getFormTabStops() {
        return Array.from(this.querySelectorAll(this.focusableSelector)).filter((element) => this.isPageFocusable(element));
      }

      onPageTabKeydown(event) {
        if (event.key !== 'Tab' || event.defaultPrevented) return;
        if (!this.isCollectionSearch() || this.hasAttribute('results')) return;

        const active = document.activeElement;
        if (!active || active === document.body) return;
        if (active.closest('[aria-modal="true"], [role="dialog"]')) return;

        const focusables = this.getPageFocusables();
        const formStops = this.getFormTabStops();
        const inForm = this.contains(active);
        const index = focusables.indexOf(active);
        const formIndex = formStops.indexOf(active);

        if (!event.shiftKey) {
          if (inForm) {
            if (formIndex >= 0 && formIndex < formStops.length - 1) return;

            const next = focusables[0];
            if (!next) return;
            event.preventDefault();
            next.focus();
            return;
          }

          if (index >= 0 && index < focusables.length - 1) {
            event.preventDefault();
            focusables[index + 1].focus();
          }

          return;
        }

        if (index > 0) {
          event.preventDefault();
          focusables[index - 1].focus();
          return;
        }

        if (index === 0) {
          event.preventDefault();
          (formStops[formStops.length - 1] || this.input)?.focus();
        }
      }

      getResultSection(element) {
        return element?.closest('[data-predictive-search-section]');
      }

      getResultSectionLabel(section) {
        if (!section) return '';

        const labelledBy = section.getAttribute('aria-labelledby');
        if (!labelledBy) return '';

        return document.getElementById(labelledBy)?.textContent?.trim() || '';
      }

      updateSectionFirstLinkDescriptions() {
        if (!this.resultsContainer || !this.hasAttribute('results')) return;

        this.resultsContainer.querySelectorAll('[data-predictive-search-section]').forEach((section) => {
          const sectionLabel = this.getResultSectionLabel(section);
          const items = this.getFocusableItems(section);

          items.forEach((item, index) => {
            if (index === 0 && sectionLabel) {
              if (!item.hasAttribute('data-predictive-search-original-label')) {
                item.setAttribute(
                  'data-predictive-search-original-label',
                  item.hasAttribute('aria-label') ? item.getAttribute('aria-label') : ''
                );
              }

              const linkName = item.getAttribute('data-predictive-search-original-label') || item.textContent.trim();
              item.setAttribute('aria-label', `${sectionLabel}, ${linkName}`);
              return;
            }

            if (!item.hasAttribute('data-predictive-search-original-label')) return;

            const originalLabel = item.getAttribute('data-predictive-search-original-label');

            if (originalLabel) {
              item.setAttribute('aria-label', originalLabel);
            } else {
              item.removeAttribute('aria-label');
            }

            item.removeAttribute('data-predictive-search-original-label');
          });
        });
      }

      resetSectionAnnouncement() {
        this.lastAnnouncedSection = null;
        this.holdStatusAnnouncement();

        if (this.sectionStatus) {
          this.sectionStatus.textContent = '';
        }
      }

      holdStatusAnnouncement() {
        this.announceToken += 1;
        this.clearStatusMessageTimers();
      }

      clearStatusMessageTimers() {
        if (this.statusMessageTimeout) {
          clearTimeout(this.statusMessageTimeout);
          this.statusMessageTimeout = null;
        }
      }

      getResultsCountMessage() {
        if (!this.resultsContainer) return '';

        const emptyHeading = this.resultsContainer.querySelector('.drawer__empty-text');
        if (emptyHeading) {
          return emptyHeading.textContent.trim();
        }

        const liveRegionEl = this.resultsContainer.querySelector(
          '[data-predictive-search-live-region-count-value]'
        );
        const liveRegionValue =
          liveRegionEl?.getAttribute('data-status-text')?.trim() ||
          liveRegionEl?.textContent?.trim();

        if (liveRegionValue) return liveRegionValue;

        const count = this.getResultLinkCount();
        return count === 1 ? `${count} result` : `${count} results`;
      }

      waitForResultMediaToSettle() {
        const images = this.resultsContainer
          ? Array.from(this.resultsContainer.querySelectorAll('img'))
          : [];
        const pending = images.filter((image) => !image.complete);

        if (pending.length === 0) return Promise.resolve();

        return Promise.race([
          Promise.all(
            pending.map(
              (image) =>
                new Promise((resolve) => {
                  image.addEventListener('load', resolve, { once: true });
                  image.addEventListener('error', resolve, { once: true });
                })
            )
          ),
          new Promise((resolve) => {
            setTimeout(resolve, 500);
          })
        ]);
      }

      // The count node is role="alert" in first-paint markup. VoiceOver binds
      // live regions at creation time and ignores later role swaps, and it also
      // ignores polite status while the search field is focused. Only mutate
      // text after typing pause + result settle. Do not write section labels
      // here (that would assertive-interrupt focused result links).
      setLiveRegionText(statusText) {
        if (!this.sectionStatus || !statusText) return;

        const token = this.announceToken;
        this.clearStatusMessageTimers();

        const write = () => {
          if (token !== this.announceToken || !this.sectionStatus) return;

          this.sectionStatus.textContent = '';

          this.statusMessageTimeout = setTimeout(() => {
            if (token !== this.announceToken || !this.sectionStatus) return;
            this.sectionStatus.textContent = statusText;
            this.statusMessageTimeout = null;
          }, 150);
        };

        this.statusMessageTimeout = setTimeout(write, 50);
      }

      announceResultsCount() {
        if (!this.sectionStatus || !this.resultsContainer) return;

        const token = ++this.announceToken;

        const apply = () => {
          if (token !== this.announceToken) return;
          if (!this.sectionStatus || !this.resultsContainer || !this.hasAttribute('results')) return;

          const statusText = this.getResultsCountMessage();
          if (!statusText) return;

          this.setLiveRegionText(statusText);
        };

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.waitForResultMediaToSettle().then(() => {
              if (token !== this.announceToken) return;
              this.statusMessageTimeout = setTimeout(apply, 50);
            });
          });
        });
      }

      announceResultSection(element) {
        const section = this.getResultSection(element);
        if (!section) return;

        const sectionKey = section.getAttribute('data-predictive-search-section');
        if (!sectionKey) return;

        const items = this.getFocusableItems(section);
        if (items[0] === element) {
          this.lastAnnouncedSection = sectionKey;
          return;
        }

        if (sectionKey === this.lastAnnouncedSection) return;

        this.lastAnnouncedSection = sectionKey;
      }

      onFocusIn(event) {
        this.pendingTab = false;
        this.open();
        this.syncResultsState();

        if (
          this.hasAttribute('results') &&
          this.resultsContainer?.contains(event.target) &&
          this.resultItems.includes(event.target)
        ) {
          this.announceResultSection(event.target);
        }
      }

      getSearchResultLink(target) {
        const link = target?.closest?.('a[href]');
        if (!link) return null;

        if (this.resultsContainer?.contains(link) || this.recommendationContainer?.contains(link)) {
          return link;
        }

        return null;
      }

      bindResultPressWindowListeners() {
        window.addEventListener('pointerup', this.onWindowResultPointerUp, true);
        window.addEventListener('pointercancel', this.onWindowResultPointerCancel, true);
      }

      unbindResultPressWindowListeners() {
        window.removeEventListener('pointerup', this.onWindowResultPointerUp, true);
        window.removeEventListener('pointercancel', this.onWindowResultPointerCancel, true);
      }

      clearResultPress() {
        this.unbindResultPressWindowListeners();
        if (this.resultRetainTimeout) {
          clearTimeout(this.resultRetainTimeout);
          this.resultRetainTimeout = null;
        }
        this.resultPress = null;
      }

      isSearchUiFocused(element = document.activeElement) {
        if (!element || element === document.body) return false;
        if (this.contains(element)) return true;
        if (this.resultsContainer?.contains(element)) return true;
        return Boolean(this.closest('search-drawer')?.contains(element));
      }

      closeIfFocusOutside() {
        if (this.isSearchUiFocused()) {
          this.syncResultsState();
          return;
        }

        this.close();
      }

      onResultPointerDown(event) {
        this.unbindResultPressWindowListeners();
        if (this.resultRetainTimeout) {
          clearTimeout(this.resultRetainTimeout);
          this.resultRetainTimeout = null;
        }

        const link = this.getSearchResultLink(event.target);
        if (!link) {
          this.resultPress = null;
          return;
        }

        this.resultPress = {
          pointerId: event.pointerId,
          link,
          x: event.clientX,
          y: event.clientY
        };
        this.bindResultPressWindowListeners();
      }

      onWindowResultPointerUp(event) {
        const press = this.resultPress;
        if (!press || press.pointerId !== event.pointerId) return;

        this.unbindResultPressWindowListeners();

        const upLink = this.getSearchResultLink(event.target);
        const moved = Math.hypot(event.clientX - press.x, event.clientY - press.y) > 16;

        if (upLink === press.link && !moved) {
          // Keep results visible until the native click navigates. Safari can
          // fire focusout after pointerup and would otherwise hide the list
          // before the PDP loads.
          this.resultPress = { link: press.link, pointerId: press.pointerId };
          this.resultRetainTimeout = setTimeout(() => {
            this.resultPress = null;
            this.resultRetainTimeout = null;
            this.closeIfFocusOutside();
          }, 2000);
          return;
        }

        this.resultPress = null;
        this.closeIfFocusOutside();
      }

      onWindowResultPointerCancel(event) {
        const press = this.resultPress;
        if (!press || press.pointerId !== event.pointerId) return;

        this.clearResultPress();
        this.closeIfFocusOutside();
      }

      onWindowPageShow() {
        this.clearResultPress();
      }

      onTabKeydown(event) {
        if (event.key !== 'Tab') return;
        this.pendingTab = true;

        if (event.defaultPrevented || !this.hasAttribute('results')) return;

        const items = this.resultItems;
        if (items.length === 0) return;

        const currentIndex = this.getResultItemIndex(event.target);
        const fromClear = Boolean(this.resetButton?.contains(event.target));

        if (event.shiftKey) {
          if (currentIndex > 0) {
            event.preventDefault();
            this.moveFocusInside(items[currentIndex - 1]);
            return;
          }

          if (currentIndex === 0) {
            event.preventDefault();
            this.moveFocusInside(this.resetButton?.tabIndex >= 0 ? this.resetButton : this.input);
          }

          return;
        }

        // Safari on /search can skip the absolute, overflow-clipped dropdown
        // after Clear or after the first suggestion. Walk the result list
        // ourselves; Tab from the last item still leaves the popup.
        if (fromClear) {
          event.preventDefault();
          this.moveFocusInside(items[0]);
          return;
        }

        if (currentIndex >= 0 && currentIndex < items.length - 1) {
          event.preventDefault();
          this.moveFocusInside(items[currentIndex + 1]);
          return;
        }

        // Overlay on /search can cover Filter and the result grid. Resolve a
        // real next tab stop first; only then close and move. If none exists,
        // leave the popup open so native Tab is not starting from inert content.
        if (currentIndex === items.length - 1 && this.isCollectionSearch()) {
          const next = this.getNextPageFocusable();
          if (!next) return;

          event.preventDefault();
          this.close();
          this.pendingTab = false;
          next.focus();
        }
      }

      onFocusOut(event) {
        if (this.resultPress || this.keepResultsOpen) return;

        // Safari can fire focusout before the next in-form control (Clear, a
        // result link) is focused. Closing then makes results inert/hidden, so
        // the following Tab leaves the drawer or the /search dropdown.
        if (event.relatedTarget && this.isSearchUiFocused(event.relatedTarget)) {
          this.pendingTab = false;
          this.syncResultsState();
          return;
        }

        const pendingTab = this.pendingTab;
        this.pendingTab = false;

        const finishFocusOut = () => {
          this.focusOutTimeout = null;

          if (this.resultPress || this.isSearchUiFocused()) {
            this.syncResultsState();
            return;
          }

          this.close();
        };

        clearTimeout(this.focusOutTimeout);
        this.focusOutTimeout = setTimeout(() => {
          if (this.resultPress || this.isSearchUiFocused()) {
            this.focusOutTimeout = null;
            this.syncResultsState();
            return;
          }

          // Tab into a result link is slower on the search page popup than in
          // the drawer. Recheck once more before closing.
          if (pendingTab) {
            this.focusOutTimeout = setTimeout(finishFocusOut, 100);
            return;
          }

          finishFocusOut();
        }, 0);
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus() {
        this.open();
        this.syncResultsState();

        const searchTerm = this.getQuery();
        if (searchTerm.length === 0) return;

        const url = this.buildUrl().toString();

        // Skip a redundant results swap when this query is already rendered.
        // VoiceOver can re-announce the focused field after a live-region
        // update; refetching would replace the results tree and cut off speech.
        if (this.lastRenderedUrl === url) {
          if (!this.hasAttribute('results')) {
            this.setResultsState(true);
          }
          return;
        }

        this.renderSection(url);
      }

      onInput() {
        this.updateResetButtonState();

        if (this.getQuery().length === 0) {
          this.lastRenderedUrl = null;
          this.setResultsState(false);
          this.resetSectionAnnouncement();
          return;
        }

        this.holdStatusAnnouncement();
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.abortController?.abort();
        this.lastRenderedUrl = null;
        this.input.value = '';
        this.updateResetButtonState();
        this.close();
        this.input.focus();
      }

      getQuery() {
        return this.input.value.trim();
      }

      buildUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      onInputKeydown(event) {
        if (!this.hasAttribute('results')) {
          if (event.key === 'Escape' && this.isCollectionSearch()) {
            event.preventDefault();
            this.close();
          }

          return;
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();

          const targetItem = this.suggestionItems[0] || this.resultItems[0];
          if (targetItem) {
            this.moveFocusInside(targetItem);
          }
        }

        // Do not wrap from the input to the last result. Mirror ArrowDown clamping
        // at the ends of the result list (Down from input → first; Up from first → input).
        if (event.key === 'ArrowUp') {
          event.preventDefault();
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        }
      }

      onResultsKeydown(event) {
        const resultItems = this.resultItems;
        const currentIndex = this.getResultItemIndex(event.target);

        if (currentIndex === -1) return;

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.focusResultItem(Math.min(currentIndex + 1, resultItems.length - 1));
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();

          if (currentIndex === 0) {
            this.moveFocusInside(this.input);
            return;
          }

          this.focusResultItem(currentIndex - 1);
        }

        if (event.key === 'Home') {
          event.preventDefault();
          this.focusResultItem(0);
        }

        if (event.key === 'End') {
          event.preventDefault();
          this.focusResultItem(resultItems.length - 1);
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          this.input.focus();
        }
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();
        this.open();
        this.syncResultsState();

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      renderSection(url) {
        this.holdStatusAnnouncement();

        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url)
          : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        if (!this.renderSearchResults(responseText)) return;

        this.lastRenderedUrl = url;
        this.setBusyState(false);
        this.setResultsState(true);
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        this.setBusyState(true);

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            if (!this.renderSearchResults(responseText)) return;

            this.cachedMap.set(url, responseText);
            this.lastRenderedUrl = url;

            this.setBusyState(false);
            this.setResultsState(true);
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error(error);
            }

            this.setBusyState(false);
          });
      }

      renderSearchResults(responseText) {
        if (this.resultsContainer === null) return false;

        const parsedResults = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(this.resultsContainer.id);

        if (!parsedResults) return false;

        this.lastAnnouncedSection = null;
        this.resultsContainer.innerHTML = parsedResults.innerHTML;

        return true;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        const form = this.closest('form[is="predictive-search"]');
        if (typeof form?.close === 'function') {
          form.close();
          return;
        }

        document.body.classList.remove('predictive-search-open');
      }
    }
  );
}
